import { useState, useEffect, useRef } from 'react';
import { JobData } from '../api/JobsService';
import { getWithExpiry } from '../utils/sessionStorage';

interface InterviewState {
  selectedJob: JobData | null;
  selectedDateRange: string;
  displayedCards: number;
  isQuestionCanvasOpen: boolean;
  selectedQuestions: number | null;
  appliedQuestions: number | null;
  selectedQuestionnaireId: string | null;
  appliedQuestionnaireId: string | null;
}

// Check for cached expanded interview data to initialize with
const getCachedInitialValues = () => {
  const cachedExpandedData = getWithExpiry('expandedInterviewData');
  if (cachedExpandedData?.filters) {
    console.log('🔄 Using cached values for initial state:', cachedExpandedData.filters);
    return {
      selectedJob: cachedExpandedData.filters.selectedJob || null,
      selectedDateRange: cachedExpandedData.filters.selectedDateRange || 'Last 30 Days',
      appliedQuestions: cachedExpandedData.filters.appliedQuestions || null,
      displayedCards: cachedExpandedData.interviewDataState?.displayedCards || 9
    };
  }
  return {
    selectedJob: null,
    selectedDateRange: 'Last 30 Days',
    appliedQuestions: null,
    displayedCards: 9
  };
};

const initialCachedValues = getCachedInitialValues();

const persistentState = {
  selectedJob: initialCachedValues.selectedJob,
  selectedDateRange: initialCachedValues.selectedDateRange,
  displayedCards: initialCachedValues.displayedCards,
  isQuestionCanvasOpen: false,
  selectedQuestions: null as number | null,
  appliedQuestions: initialCachedValues.appliedQuestions,
  selectedQuestionnaireId: null as string | null,
  appliedQuestionnaireId: null as string | null
};


// purpose: to provide a good ux, we save state
export const usePersistedInterviewState = () => {
  const [selectedJob, setSelectedJob] = useState<JobData | null>(persistentState.selectedJob);
  const [selectedDateRange, setSelectedDateRange] = useState<string>(persistentState.selectedDateRange);
  const [displayedCards, setDisplayedCards] = useState(persistentState.displayedCards);
  const [isQuestionCanvasOpen, setIsQuestionCanvasOpen] = useState(persistentState.isQuestionCanvasOpen);
  const [selectedQuestions, setSelectedQuestions] = useState<number | null>(persistentState.selectedQuestions);
  const [appliedQuestions, setAppliedQuestions] = useState<number | null>(persistentState.appliedQuestions);
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<string | null>(persistentState.selectedQuestionnaireId);
  const [appliedQuestionnaireId, setAppliedQuestionnaireId] = useState<string | null>(persistentState.appliedQuestionnaireId);

  // Update persistent state whenever local state changes
  useEffect(() => {
    persistentState.selectedJob = selectedJob;
    persistentState.selectedDateRange = selectedDateRange;
    persistentState.displayedCards = displayedCards;
    persistentState.isQuestionCanvasOpen = isQuestionCanvasOpen;
    persistentState.selectedQuestions = selectedQuestions;
    persistentState.appliedQuestions = appliedQuestions;
    persistentState.selectedQuestionnaireId = selectedQuestionnaireId;
    persistentState.appliedQuestionnaireId = appliedQuestionnaireId;
  }, [selectedJob, selectedDateRange, displayedCards, isQuestionCanvasOpen, selectedQuestions, appliedQuestions, selectedQuestionnaireId, appliedQuestionnaireId]);

  return {
    selectedJob, setSelectedJob,
    selectedDateRange, setSelectedDateRange,
    displayedCards, setDisplayedCards,
    isQuestionCanvasOpen, setIsQuestionCanvasOpen,
    selectedQuestions, setSelectedQuestions,
    appliedQuestions, setAppliedQuestions,
    selectedQuestionnaireId, setSelectedQuestionnaireId,
    appliedQuestionnaireId, setAppliedQuestionnaireId
  };
};